import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { observable, action, computed } from "mobx";
import { FlightConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { Travelers } from "src/components/shared/TravelersField/typings";
import { TypeaheadSelectionProps } from "components/flexComponents/WizardHotelPWA/typings";
import { locationState } from "src/stores/wizard/state/global/location";
import { dateState } from "src/stores/wizard/state/global/date";
import { FlightLegState } from "stores/wizard/state/flight/MultiLegFlights/typings";
import { setCookie } from "components/utility/cookie";
import { setHotelPartialStayDate } from "stores/wizard/state/global/date/dateUtils";
import { FlightLastSearchData, LastSearchData } from "stores/wizard/LastSearchData";
import { DateState, TravelersState } from "stores/wizard/state/global";
import { formatDate } from "bernie-l10n";
import { travelersMetadata } from "src/components/shared/TravelersField/utils";
import { Location } from "src/stores/wizard/state/global/location/typings.ts";
import { FlightTypeCodeMap } from "src/stores/wizard/state/flight/typings";
import * as React from "react";
import { ClassCodes } from "src/components/shared/PreferredClassInput/PreferredClassInput";
import { PreferredAirline } from "src/components/shared/PreferredAirlineInput/PreferredAirlineInput";
import { processPrefetch } from "src/utils/PrefetchFSRUtils";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { FlightType } from "../typings";
import { travelersStateOnConfig } from "../global/travelers/travelersState";
import { DestinationModifier } from "stores/wizard/state/DestinationModifier";

export class FlightWizardState extends DestinationModifier implements LOBState {
  private globalState: GlobalWizardState;

  @computed
  public get canTrack() {
    return this.globalState.canTrack;
  }

  public get isDesktop() {
    return this.globalState.isDesktop;
  }

  @computed
  public get useLastSearchForMultiLOB() {
    return this.globalState.config.useLastSearchForMultiLOB;
  }

  @computed
  public get travelersValueChanged() {
    return this.globalState.travelersValueChanged;
  }

  @action
  public setTravelersValue = () => {
    this.globalState.setTravelersValue();
  };

  public get useLastSearch() {
    return this.useLastSearchForMultiLOB || this.config.lastSearch.useLastSearchForSingleLOB;
  }

  public lastSearchData?: FlightLastSearchData;

  @observable
  public errorInputRef: React.RefObject<HTMLHeadingElement> | null;

  @observable
  public errorSummaryRef: React.RefObject<HTMLInputElement> | null;

  @observable
  public isRoundtripFormValid = true;

  @observable
  public isOnewayFormValid = true;

  @observable
  public isMulticityFormValid = true;

  @computed
  public get flightClassCode() {
    return this.globalState.flightClassCode;
  }

  @observable
  public isAddAHotelChecked = false;

  @computed
  public get location() {
    return this.globalState.location;
  }

  @observable
  public config: FlightConfig;

  @observable
  public subLOBState: FlightType = FlightType.ROUND_TRIP;

  @observable
  public legsDateStartInvalidKey: string[];

  @observable
  public listOfAdditionalLegs: FlightLegState[];

  @observable
  public multiCityFlightLegURL: string[];

  @observable
  public isAddACarChecked = false;

  @observable
  public isNonStopFlightChecked = false;

  @observable
  public numberOfErrors = 0;

  public localDateFormat: string;

  @action
  public buildMultiCityFlightLegUrl = (legIndex: number) => {
    const legConfig = this.config.multiCityFlight.legs[legIndex];
    const rawFormatForSubmission = legConfig.date.ISOSubmissionSupport ? legConfig.date.format : this.localDateFormat;

    const { value: origin, metaData: originMetaData } = this.listOfAdditionalLegs[legIndex].location.origin;
    const { value: destination, metaData: destinationMetadata } = this.listOfAdditionalLegs[
      legIndex
    ].location.destination;

    const { ttla: originTtla } = originMetaData;
    const { ttla: destinationTtla } = destinationMetadata;
    const formattedOriginTtla = originTtla ? `(${originTtla})` : "";
    const formattedDestinationTtla = destinationTtla ? `(${destinationTtla})` : "";

    const from = `from:${origin}${formattedOriginTtla},`;
    const to = `to:${destination}${formattedDestinationTtla},`;
    const startDate = formatDate(this.listOfAdditionalLegs[legIndex].date.start, {
      raw: rawFormatForSubmission,
    });
    const departure = `departure:${startDate}TANYT`;

    return from + to + departure;
  };

  @action
  public updateLegLocationOrigin(selection: TypeaheadSelection, index: number) {
    const { term, data } = selection as TypeaheadSelectionProps;
    const legToUpdate = this.listOfAdditionalLegs[index];
    legToUpdate.location.origin.value = term;
    legToUpdate.location.origin.metaData.regionId = data && data.regionId;
    legToUpdate.location.origin.metaData.ttla =
      data && data.hierarchyInfo && data.hierarchyInfo.airport && data.hierarchyInfo.airport.airportCode;
    this.multiCityFlightLegURL[index] = this.buildMultiCityFlightLegUrl(index);
  }

  @action
  public updateLegLocationDestination(selection: TypeaheadSelection, index: number) {
    const { term, data } = selection as TypeaheadSelectionProps;
    const legToUpdate = this.listOfAdditionalLegs[index];
    legToUpdate.location.destination.value = term;
    legToUpdate.location.destination.metaData.regionId = data && data.regionId;
    legToUpdate.location.destination.metaData.ttla =
      data && data.hierarchyInfo && data.hierarchyInfo.airport && data.hierarchyInfo.airport.airportCode;
    this.multiCityFlightLegURL[index] = this.buildMultiCityFlightLegUrl(index);

    //copy into origin of next leg if that leg exists
    const nextLegExists = index + 1 < this.listOfAdditionalLegs.length;
    if (nextLegExists) {
      this.copyOneLocationIntoAnotherLocation(
        this.listOfAdditionalLegs[index].location.destination,
        this.listOfAdditionalLegs[index + 1].location.origin
      );
    }
  }

  @action
  public updateLegDate = (start: Date, end: Date, index: number) => {
    const legToUpdate = this.listOfAdditionalLegs[index];
    legToUpdate.date.start = start;
    legToUpdate.date.end = end;
    this.multiCityFlightLegURL[index] = this.buildMultiCityFlightLegUrl(index);
  };

  @observable
  public hotelDate: DateState;

  @action
  public updateSubLOBState(subLOBState: FlightType) {
    this.subLOBState = subLOBState;
  }

  @computed
  public get date() {
    return this.globalState.date;
  }

  @observable
  public flightsTravelers: TravelersState;

  @computed
  public get travelers() {
    return this.flightsTravelers;
  }

  @computed
  public get hotelTravelersMetadata() {
    return travelersMetadata(this.travelers.hotel);
  }

  @computed
  public get nonHotelTravelersMetadata() {
    return travelersMetadata(this.travelers.nonHotel);
  }

  @computed
  public get lastAdditionalLegDate() {
    return this.listOfAdditionalLegs?.length
      ? this.listOfAdditionalLegs[this.listOfAdditionalLegs.length - 1].date.start
      : null;
  }

  @computed
  public get cruiseTravelersMetadata() {
    return travelersMetadata(this.travelers.cruise);
  }

  @computed
  public get flightTripType() {
    return this.subLOBState;
  }

  @computed
  public get flightSoftPackageTripType() {
    const { roundtrip, oneway } = FlightTypeCodeMap;
    if (this.subLOBState === FlightType.ROUND_TRIP) {
      return roundtrip;
    }
    if (this.subLOBState === FlightType.ONE_WAY) {
      return oneway;
    }

    return "";
  }

  @computed
  public get flightPackageType() {
    if (this.isAddAHotelChecked && this.isAddACarChecked) {
      return "fhc";
    }
    if (this.isAddAHotelChecked) {
      return "fh";
    }
    if (this.isAddACarChecked) {
      return "fc";
    }
    return "";
  }

  public wizardInputsArray: React.RefObject<HTMLInputElement>[] = [];

  //validations
  @observable
  public originInvalidKey: string | "";

  @action findFocusElement = (keyword: string) =>
    this.wizardInputsArray.find((element) => element.current !== null && element.current.outerHTML.includes(keyword));

  @action
  public validateOriginField = () => {
    if (!this.globalState.validateOriginField()) {
      this.originInvalidKey = this.config.location.origin.invalidLocationMessageKey;
      this.errorInputRef = this.findFocusElement("origin")!;

      return false;
    }

    this.originInvalidKey = "";

    return true;
  };

  @action moreThanOneError = () => {
    return this.globalState.moreThanOneError(this.numberOfErrors);
  };

  @observable
  public destinationInvalidKey: string | "";

  @action
  public validateDestinationField = () =>
    this.validateDestinationFieldHasValue() && this.validateOriginDifferentFromDestination();

  @action
  public validateDestinationFieldHasValue = () => {
    if (!this.globalState.validateDestinationField()) {
      // if the origin field is valid I want to focus the invalid destination else the focus should be made in the origin validation block
      if (this.globalState.validateOriginField()) {
        if (!this.errorInputRef) {
          this.errorInputRef = this.findFocusElement("destination")!;
        }
      }
      this.destinationInvalidKey = this.config.location.destination.invalidLocationMessageKey;

      return false;
    }
    this.destinationInvalidKey = "";

    return true;
  };

  @action
  public validateOriginDifferentFromDestination = () => {
    if (!this.globalState.validateOriginDifferentFromDestination()) {
      this.destinationInvalidKey = this.config.location.destination.invalidOriginSameAsDestinationKey!;

      return false;
    }
    this.destinationInvalidKey = "";

    return true;
  };

  @observable
  public hotelTravelersInvalidKey = "";

  @observable
  public nonHotelTravelersInvalidKey = "";

  @action
  public validateLessThanNTravelers = () => {
    if (
      this.isAddAHotelChecked &&
      !this.globalState.validateLessThanNTravelers(this.hotelTravelersMetadata, this.config.travelersWithHotel)
    ) {
      this.hotelTravelersInvalidKey = this.config.travelersWithHotel.invalidLessThanNTravelersMessageToken;

      return false;
    }
    if (!this.globalState.validateLessThanNTravelers(this.nonHotelTravelersMetadata, this.config.travelers)) {
      this.nonHotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

      return false;
    }

    return true;
  };

  @action
  public validateUnattendedInfantInLap = () => {
    if (
      this.isAddAHotelChecked &&
      !this.globalState.validateUnattendedInfantInLap(
        this.hotelTravelersMetadata,
        this.config.travelersWithHotel,
        this.travelers.hotel.infantSeating
      )
    ) {
      this.hotelTravelersInvalidKey = this.config.travelersWithHotel.invalidUnattendedInfantOnLapMessageToken;

      return false;
    }
    if (
      !this.globalState.validateUnattendedInfantInLap(
        this.nonHotelTravelersMetadata,
        this.config.travelers,
        this.travelers.nonHotel.infantSeating
      )
    ) {
      this.nonHotelTravelersInvalidKey = this.config.travelers.invalidUnattendedInfantOnLapMessageToken;

      return false;
    }

    return true;
  };

  @action
  public validateInfantsPerTraveler = () => {
    if (
      this.isAddAHotelChecked &&
      !this.globalState.validateInfantsPerTraveler(
        this.hotelTravelersMetadata,
        this.config.travelersWithHotel,
        this.travelers.hotel.infantSeating
      )
    ) {
      this.hotelTravelersInvalidKey = this.config.travelersWithHotel.invalidNumberOfInfantPerTravelerMessageToken!;

      return false;
    }
    if (
      !this.globalState.validateInfantsPerTraveler(
        this.nonHotelTravelersMetadata,
        this.config.travelers,
        this.travelers.nonHotel.infantSeating
      )
    ) {
      this.nonHotelTravelersInvalidKey = this.config.travelers.invalidNumberOfInfantPerTravelerMessageToken!;

      return false;
    }

    return true;
  };

  @action
  public validateChildInfantFields = () => {
    if (this.config.travelers.withRooms && !this.globalState.validateChildInfantFields(this.travelers)) {
      this.hotelTravelersInvalidKey = this.config.travelers.invalidChildInfantValuesMessageToken!;
      return false;
    }

    if (!this.config.travelers.withRooms && !this.globalState.validateChildInfantFields(this.travelers)) {
      this.nonHotelTravelersInvalidKey = this.config.travelers.invalidChildInfantValuesMessageToken!;
      return false;
    }
    return true;
  };

  @action
  public validateTravelersField = () =>
    this.validateLessThanNTravelers() &&
    this.validateUnattendedInfantInLap() &&
    this.validateInfantsPerTraveler() &&
    this.validateChildInfantFields();

  @observable
  public dateStartInvalidKey = "";

  @observable
  public dateEndInvalidKey = "";

  @action
  public validateMaxDateRange = () => {
    switch (this.subLOBState) {
      case FlightType.ROUND_TRIP:
        if (!this.globalState.validateMaxDateRange(this.date.start, this.date.end, this.config.date.maxDaysRange)) {
          this.dateEndInvalidKey = this.config.date.invalidMaxDatesRangeMessageToken;
          if (!this.errorInputRef) {
            this.errorInputRef = this.findFocusElement("d2")!;
          }

          return false;
        }

        return true;
      default:
        return true;
    }
  };

  @action
  public validateEndDateField = () => this.validateMaxDateRange();

  @observable
  public hotelStartDateInvalidKey = "";

  @action
  public validateHotelStartDateWithinRange = () => {
    switch (this.subLOBState) {
      case FlightType.ROUND_TRIP:
        if (
          this.isAddAHotelChecked &&
          !this.globalState.validatePartialDateWithinRange(this.date.start, this.date.end, this.hotelDate.start)
        ) {
          this.hotelStartDateInvalidKey = this.config.hotelDate.invalidStartDateOutOfRangeMessageToken;
          if (!this.errorInputRef) {
            this.errorInputRef = this.findFocusElement("d1")!;
          }

          return false;
        }

        return true;
      case FlightType.ONE_WAY:
        if (this.isAddAHotelChecked && this.hotelDate.start < this.date.start) {
          this.hotelStartDateInvalidKey = this.config.invalidOnewayHotelStartDateMessageToken;
          if (!this.errorInputRef) {
            this.errorInputRef = this.findFocusElement("d1")!;
          }

          return false;
        }

        return true;
      default:
        return true;
    }
  };

  @action
  public validateHotelStartDateField = () => this.validateHotelStartDateWithinRange();

  @observable
  public hotelEndDateInvalidKey = "";

  @action
  public validateHotelMaxDateRange = () => {
    switch (this.subLOBState) {
      case FlightType.ROUND_TRIP:
      case FlightType.ONE_WAY:
        if (
          this.isAddAHotelChecked &&
          !this.globalState.validateMaxDateRange(
            this.hotelDate.start,
            this.hotelDate.end,
            this.config.hotelDate.maxDaysRange
          )
        ) {
          this.hotelEndDateInvalidKey = this.config.hotelDate.invalidMaxDatesRangeMessageToken;
          if (!this.errorInputRef) {
            this.errorInputRef = this.findFocusElement("d2")!;
          }

          return false;
        }

        return true;
      default:
        return true;
    }
  };

  @action
  public validateHotelEndDateWithinRange = () => {
    switch (this.subLOBState) {
      case FlightType.ROUND_TRIP:
        if (
          this.isAddAHotelChecked &&
          !this.globalState.validatePartialDateWithinRange(this.date.start, this.date.end, this.hotelDate.end)
        ) {
          this.hotelEndDateInvalidKey = this.config.hotelDate.invalidEndDateOutOfRangeMessageToken;
          if (!this.errorInputRef) {
            this.errorInputRef = this.findFocusElement("d2")!;
          }

          return false;
        }

        return true;
      default:
        return true;
    }
  };

  public validateHotelEndDateField = () => this.validateHotelEndDateWithinRange() && this.validateHotelMaxDateRange();

  @action
  public validateEndDateBeforeStartDateFields = () => {
    const departingDate = this.date.start;
    const returningDate = this.date.end;

    if (
      this.globalState.validateEndDateBeforeStartDateFields(departingDate, returningDate) &&
      this.subLOBState === FlightType.ROUND_TRIP
    ) {
      this.dateEndInvalidKey = this.globalState.config.date.invalidDateRangeEndBeforeStart ?? "";
      if (!this.errorInputRef) {
        this.errorInputRef = this.findFocusElement("d2") ?? null;
      }

      return false;
    }

    return true;
  };

  @action
  public resetValidations = () => {
    this.originInvalidKey = "";
    this.destinationInvalidKey = "";
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
    this.hotelStartDateInvalidKey = "";
    this.hotelEndDateInvalidKey = "";
    this.hotelTravelersInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
    const maxNumberOfAdditionalLegs = this.config.multiCityFlight.maxNumberOfAdditionalLegs;
    this.legsDateStartInvalidKey = new Array<string>(maxNumberOfAdditionalLegs).fill("", 0, maxNumberOfAdditionalLegs);
  };

  public validateForm = () => {
    this.cleanErrorState();
    const validations: boolean[] = [
      this.validateOriginField(),
      this.validateDestinationField(),
      this.validateTravelersField(),
      this.validateEndDateField(),
      this.validateHotelStartDateField(),
      this.validateHotelEndDateField(),
      this.validateEndDateBeforeStartDateFields(),
    ];

    validations.forEach((value) => !value && (this.numberOfErrors += 1));

    return !validations.includes(false);
  };

  // other actions
  @action
  public overrideConfig(callback: () => void): void {
    callback();
  }

  @action
  public updateDateSelection = (start: Date, end: Date, multiLegIndex?: number) => {
    if (multiLegIndex !== undefined) {
      this.updateLegDate(start, end, multiLegIndex);
    } else {
      this.globalState.updateDateSelection(start, end);
      this.dateStartInvalidKey = "";
      this.dateEndInvalidKey = "";
      this.hotelStartDateInvalidKey = "";
      this.hotelEndDateInvalidKey = "";
    }
  };

  @action
  public updateOriginSelection = (selection: TypeaheadSelection, index?: number) => {
    //in the case of a multi-city flight, update the individual leg
    if (index !== undefined) {
      this.updateLegLocationOrigin(selection, index);
    } else {
      this.globalState.updateOriginSelection(selection);
      this.originInvalidKey = "";
    }
  };

  public updateHotelDateSelection = (start: Date, end: Date) => {
    this.hotelDate.start = start;
    this.hotelDate.end = end;
    this.hotelStartDateInvalidKey = "";
    this.hotelEndDateInvalidKey = "";
  };

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection, index?: number) => {
    if (index !== undefined) {
      this.updateLegLocationDestination(selection, index);
    } else {
      this.globalState.updateDestinationSelection(selection);
      this.destinationInvalidKey = "";

      //copy main leg into first additional leg
      this.copyOneLocationIntoAnotherLocation(this.location.destination, this.listOfAdditionalLegs[0].location.origin);
    }
    processPrefetch();
  };

  @action
  public swapLocations() {
    this.globalState.swapOriginAndDestinationLocations();

    //update for reflection
    this.copyOneLocationIntoAnotherLocation(this.location.destination, this.listOfAdditionalLegs[0].location.origin);
  }

  @action
  public setFlightClass(flightClassCode: ClassCodes) {
    this.globalState.setFlightClass(flightClassCode);
  }

  @action
  public setFlightAirline(flightAirline: PreferredAirline) {
    this.globalState.setFlightAirline(flightAirline);
  }

  @action
  public addALeg = () => {
    if (this.listOfAdditionalLegs.length < this.config.multiCityFlight.maxNumberOfAdditionalLegs) {
      this.listOfAdditionalLegs.push({
        location: locationState(),
        date: this.globalState.getDateFromConfig(this.config.date),
      });
      const index = this.listOfAdditionalLegs.length - 1;
      const notFirstLeg = index > 0;
      setTimeout(() => {
        const focusIndex = this.wizardInputsArray.length - 2;
        this.wizardInputsArray[focusIndex]?.current?.focus();
      });
      if (notFirstLeg) {
        this.copyOneLocationIntoAnotherLocation(
          this.listOfAdditionalLegs[index - 1].location.destination,
          this.listOfAdditionalLegs[index].location.origin
        );
      }
    }
  };

  //copies a non-empty location into another location. Will overwrite contents.
  public copyOneLocationIntoAnotherLocation = (locationToCopyFrom: Location, locationToCopyInto: Location) => {
    if (locationToCopyFrom.value && locationToCopyFrom.value !== "") {
      locationToCopyInto.value = locationToCopyFrom.value;
      locationToCopyInto.metaData.countryCode = locationToCopyFrom.metaData.countryCode;
      locationToCopyInto.metaData.latLong = locationToCopyFrom.metaData.latLong;
      locationToCopyInto.metaData.regionId = locationToCopyFrom.metaData.regionId;
      locationToCopyInto.metaData.shortName = locationToCopyFrom.metaData.shortName;
      locationToCopyInto.metaData.ttla = locationToCopyFrom.metaData.ttla;
    }
  };

  @action
  public removeALeg = (index: number) => {
    if (index < this.listOfAdditionalLegs.length) {
      this.listOfAdditionalLegs.splice(index, 1);
    }
  };

  @action
  public updateDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    destinationId: string,
    hotelId: string,
    timestamp: number
  ) {
    this.globalState.updateDestinationFromCookie(
      value,
      regionID,
      countryCode,
      latLong,
      ttla,
      destinationId,
      hotelId,
      timestamp
    );
    this.destinationInvalidKey = "";
  }

  @action
  public updateOriginFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    timestamp: number
  ) {
    this.globalState.updateOriginFromCookie(value, regionID, countryCode, latLong, ttla, timestamp);
    this.originInvalidKey = "";
  }

  @action
  public updateDestinationFromContext(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    destinationId: string,
    hotelId: string
  ) {
    this.globalState.updateDestinationFromContext(value, regionID, countryCode, latLong, ttla, destinationId, hotelId);
    this.destinationInvalidKey = "";
  }

  @action
  public updateOriginFromContext(value: string, regionID: string, countryCode: string, latLong: string, ttla: string) {
    this.globalState.updateOriginFromContext(value, regionID, countryCode, latLong, ttla);
    this.originInvalidKey = "";
  }

  public setLastSearchCookie = () => {
    if (this.canTrack && this.useLastSearch) {
      const origin = this.location.origin;
      const originMetaData = this.location.origin.metaData;
      const destination = this.location.destination;
      const detinationMetaData = this.location.destination.metaData;
      const date = this.date;
      const hotelDate = this.hotelDate;
      const listOfAddtionalLegs = this.listOfAdditionalLegs;

      const lastSearchCookieObject: LastSearchData = {
        lob: this.config.lastSearch.lobKey,
        flight: {
          origin: {
            value: origin.value || "",
            regionID: origin.metaData.regionId || "",
            ttla: originMetaData.ttla || "",
            countryCode: originMetaData.countryCode || "",
            latLong: originMetaData.latLong || "",
            destinationID: originMetaData.destinationId || "",
            hotelID: originMetaData.hotelId || "",
          },
          destination: {
            value: destination.value || "",
            regionID: detinationMetaData.regionId || "",
            ttla: detinationMetaData.ttla || "",
            countryCode: detinationMetaData.countryCode || "",
            latLong: detinationMetaData.latLong || "",
            destinationID: detinationMetaData.destinationId || "",
            hotelID: originMetaData.hotelId || "",
          },
          date: {
            start: date.start ? date.start.toString() : "",
            end: date.end ? date.end.toString() : "",
          },
          hotel_date: {
            start: hotelDate.start ? hotelDate.start.toString() : "",
            end: hotelDate.end ? hotelDate.end.toString() : "",
          },
          flight_class_code: this.flightClassCode,
          add_check: {
            hotel: this.isAddAHotelChecked.toString(),
            car: this.isAddACarChecked.toString(),
          },
          list_of_additional_legs: listOfAddtionalLegs ? JSON.stringify(listOfAddtionalLegs) : "",
          travelers: JSON.stringify(this.travelers),
          subLOBState: this.subLOBState,
          timestamp: Math.floor(Date.now() / 1000),
        },
      };
      setCookie({
        key: this.config.lastSearch.lastSearchKey,
        value: JSON.stringify(lastSearchCookieObject),
        days: 30,
      });
    }
  };

  public updateStateFromContext = (context: ExtendedContextStore) => {
    const originFromContext = context.searchContext.origin;
    const originParent = originFromContext ? originFromContext.parent : undefined;
    const destinationFromContext = context.searchContext.destination;
    const destinationParent = destinationFromContext ? destinationFromContext.parent : undefined;

    if (originFromContext) {
      this.updateOriginFromContext(
        originFromContext.extendedName ? originFromContext.extendedName : "",
        originFromContext.id ? originFromContext.id : "",
        originParent && originFromContext.parent.code ? originFromContext.parent.code : "",
        originParent && originFromContext.parent.geoCoordinate.latitude && originFromContext.geoCoordinate.longitude
          ? `${originFromContext.parent.geoCoordinate.latitude},${originFromContext.parent.geoCoordinate.longitude}`
          : "",
        originFromContext.code ? originFromContext.code : ""
      );
    }

    if (destinationFromContext) {
      this.updateDestinationFromContext(
        destinationFromContext.extendedName ? destinationFromContext.extendedName : "",
        destinationFromContext.id ? destinationFromContext.id : "",
        destinationParent && destinationFromContext.parent.code ? destinationFromContext.parent.code : "",
        destinationParent &&
          destinationFromContext.parent.geoCoordinate.latitude &&
          destinationFromContext.parent.geoCoordinate.longitude
          ? `${destinationFromContext.parent.geoCoordinate.latitude},${destinationFromContext.parent.geoCoordinate.longitude}`
          : "",
        destinationFromContext.code ? destinationFromContext.code : "",
        destinationFromContext.id ? destinationFromContext.id : "",
        ""
      );
    }
  };

  public updateStateFromCookie = () => {
    const lastSearchData = this.lastSearchData;

    if (this.canTrack && this.useLastSearch && lastSearchData) {
      const originValue = decodeURIComponent(lastSearchData.origin?.value);
      const destinationValue = decodeURIComponent(lastSearchData.destination?.value);

      const startDateFromCookie = lastSearchData.date?.start ? new Date(lastSearchData.date.start) : new Date();
      const endDateFromCookie = lastSearchData.date?.end ? new Date(lastSearchData.date.end) : new Date();
      const hotelStartDateFromCookie = lastSearchData.hotel_date?.start
        ? new Date(lastSearchData.hotel_date.start)
        : new Date();
      const hotelEndDateFromCookie = lastSearchData.hotel_date?.end
        ? new Date(lastSearchData.hotel_date.end)
        : new Date();

      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      const startDateToUse = startDateFromCookie >= today ? startDateFromCookie : today;
      const endDateToUse = endDateFromCookie >= tomorrow ? endDateFromCookie : tomorrow;
      const hotelStartDateToUse = hotelStartDateFromCookie >= today ? hotelStartDateFromCookie : today;
      const hotelEndDateToUse = hotelEndDateFromCookie >= tomorrow ? hotelEndDateFromCookie : tomorrow;

      // Check if the lastSearch cookie passed had flightWizardState and travelers info, if not reload fallback from globalWizard
      const travelers: TravelersState = lastSearchData.travelers
        ? JSON.parse(lastSearchData.travelers)
        : this.travelers;

      this.subLOBState = lastSearchData.subLOBState ? (lastSearchData.subLOBState as FlightType) : this.subLOBState;

      this.updateOriginFromCookie(
        originValue !== "undefined" ? originValue : "",
        lastSearchData.origin?.regionID ? lastSearchData.origin.regionID : "",
        lastSearchData.origin?.countryCode ? lastSearchData.origin.countryCode : "",
        lastSearchData.origin?.latLong ? lastSearchData.origin.latLong : "",
        lastSearchData.origin?.ttla ? lastSearchData.origin.ttla : "",
        lastSearchData.timestamp
      );

      this.updateDestinationFromCookie(
        destinationValue !== "undefined" ? destinationValue : "",
        lastSearchData.destination?.regionID ? lastSearchData.destination.regionID : "",
        lastSearchData.destination?.countryCode ? lastSearchData.destination.countryCode : "",
        lastSearchData.destination?.latLong ? lastSearchData.destination.latLong : "",
        lastSearchData.destination?.ttla ? lastSearchData.destination.ttla : "",
        lastSearchData.destination?.destinationID ? lastSearchData.destination.destinationID : "",
        lastSearchData.destination?.hotelID ? lastSearchData.destination.hotelID : "",
        lastSearchData.timestamp
      );

      this.setFlightClass(lastSearchData.flight_class_code ? lastSearchData.flight_class_code : this.flightClassCode);

      const lastSearchCheckCarValue = lastSearchData.add_check?.car
        ? JSON.parse(lastSearchData.add_check.car)
        : this.isAddACarChecked;

      const lastSearchCheckHotelValue = lastSearchData.add_check?.hotel
        ? JSON.parse(lastSearchData.add_check.hotel)
        : this.isAddAHotelChecked;

      if (lastSearchCheckCarValue) {
        this.toggleAddCarCheckbox(); // updating from false to true
      }

      if (lastSearchCheckHotelValue) {
        this.toggleAddAHotelCheckbox();
      }

      if (this.isAddAHotelChecked) {
        const hotelTravelers = travelers.hotel;
        this.updateDateSelection(startDateToUse, endDateToUse);
        this.updateHotelDateSelection(hotelStartDateToUse, hotelEndDateToUse);
        this.updateHotelTravelersSelection(hotelTravelers);
      } else {
        const nonHotelTravelers = travelers.nonHotel;
        this.updateDateSelection(startDateToUse, endDateToUse);
        this.updateNonHotelTravelersSelection(nonHotelTravelers);
      }

      if (lastSearchData.list_of_additional_legs) {
        this.listOfAdditionalLegs = JSON.parse(lastSearchData.list_of_additional_legs).map((leg: FlightLegState) => {
          return {
            ...leg,
            date: {
              start: new Date(leg.date.start),
              end: new Date(leg.date.end),
            },
          };
        });

        this.listOfAdditionalLegs.forEach((leg: FlightLegState, index: number) => {
          this.multiCityFlightLegURL[index] = this.buildMultiCityFlightLegUrl(index);
        });
      } else {
        this.listOfAdditionalLegs = new Array<FlightLegState>(this.config.multiCityFlight.maxNumberOfAdditionalLegs);
      }
    }
  };

  @action
  public submit = (event: React.FormEvent) => {
    const isValidForm = this.validateForm();
    if (!isValidForm) {
      event.preventDefault();
      this.updateInvalidFormsState(false);
      this.errorSummaryRef = this.findFocusElement("error")!;
      this.errorSummaryRef?.current?.focus();
    } else if (this.canTrack && this.useLastSearch) {
      this.setLastSearchCookie();
      this.updateInvalidFormsState(true);
    }
    this.globalState.submit();
  };

  /**
   * Cleans error state in all forms
   */
  @action
  public cleanErrorState = () => {
    if (this.errorInputRef) {
      this.errorInputRef = null;
    }
    this.numberOfErrors = 0;
    this.isOnewayFormValid = true;
    this.isMulticityFormValid = true;
    this.isRoundtripFormValid = true;
  };

  /**
   * Resets other flight forms to not have errors when you change to a different flight type
   * Shows error message in the right flight type
   */
  @action
  public updateInvalidFormsState = (validState?: boolean) => {
    const state = validState !== undefined ? validState : true;
    switch (this.flightTripType) {
      case FlightType.ROUND_TRIP:
        this.isRoundtripFormValid = state;
        this.isOnewayFormValid = true;
        this.isMulticityFormValid = true;
        break;
      case FlightType.ONE_WAY:
        this.isOnewayFormValid = state;
        this.isRoundtripFormValid = true;
        this.isMulticityFormValid = true;
        break;
      default:
        this.isMulticityFormValid = state;
        this.isRoundtripFormValid = true;
        this.isOnewayFormValid = true;
        break;
    }
  };

  @action
  public updateHotelTravelersSelection = (travelers: Travelers) => {
    this.globalState.updateHotelTravelersSelection(travelers, this.travelers);

    if (this.lastSearchData && this.lastSearchData.travelers && this.validateTravelersField()) {
      this.lastSearchData.travelers = JSON.stringify(this.travelers);
    }

    this.hotelTravelersInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
  };

  @action
  public updateNonHotelTravelersSelection = (travelers: Travelers) => {
    this.globalState.updateNonHotelTravelersSelection(travelers, this.travelers);

    if (this.lastSearchData && this.lastSearchData.travelers && this.validateTravelersField()) {
      this.lastSearchData.travelers = JSON.stringify(this.travelers);
    }

    this.hotelTravelersInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
  };

  @action
  public toggleAddAHotelCheckbox = () => {
    this.isAddAHotelChecked = !this.isAddAHotelChecked;
    setHotelPartialStayDate(this.date, this.hotelDate);
    this.hotelTravelersInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
  };

  @action
  public toggleAddCarCheckbox = () => {
    this.isAddACarChecked = !this.isAddACarChecked;
  };

  @action
  public toggleNonStopFlightCheckbox = () => {
    this.isNonStopFlightChecked = !this.isNonStopFlightChecked;
  };

  /**
   * dynamically build additional flight leg list from min number in config
   * Add one less than the min number to the array, as the first leg is in this.location
   */
  private populateFlightLegs = () => {
    this.listOfAdditionalLegs = [];
    for (let i = 0; i < this.config.multiCityFlight.minNumberOfLegs - 1; i++) {
      const newLeg: FlightLegState = {
        location: locationState(),
        date: this.globalState.getDateFromConfig(this.config.date),
      };
      this.listOfAdditionalLegs.push(newLeg);
    }
  };

  public updateDateFromConfig = () => {
    this.globalState.updateDateFromConfig(this.config.date);
  };

  constructor(config: FlightConfig, globalState: GlobalWizardState, lastSearchData?: LastSearchData) {
    super();
    this.config = config;
    this.globalState = globalState;
    this.flightsTravelers = travelersStateOnConfig(this.config.travelers);
    this.populateFlightLegs();
    const maxNumberOfAdditionalLegs = this.config.multiCityFlight.maxNumberOfAdditionalLegs;
    this.legsDateStartInvalidKey = new Array<string>(maxNumberOfAdditionalLegs).fill("", 0, maxNumberOfAdditionalLegs);
    this.multiCityFlightLegURL = new Array<string>(maxNumberOfAdditionalLegs);
    this.hotelDate = dateState();
    if (lastSearchData && this.canTrack) {
      this.lastSearchData = lastSearchData.flight;
    }
  }
}
